/**
 * OpenSolar Project Detail Page
 *
 * This page displays detailed information about a specific OpenSolar project.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'wouter';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { StatusBadge } from '@/components/opensolar/project-status-badge';
import {
  ArrowLeft,
  Calendar,
  ExternalLink,
  FileText,
  Loader2,
  MapPin,
  Phone,
  RefreshCw,
  User,
} from 'lucide-react';
import { Link } from 'wouter';

export default function OpenSolarProjectDetailPage() {
  const { id } = useParams();
  const { toast } = useToast();
  const projectId = parseInt(id || '0');

  // Fetch project details
  const {
    data: project,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['/api/opensolar/projects', projectId],
    queryFn: async () => {
      try {
        const response = await fetch(`/api/opensolar/projects/${projectId}`);
        if (!response.ok) {
          throw new Error('Failed to fetch project details');
        }
        return await response.json();
      } catch (error) {
        console.error('Error fetching OpenSolar project details:', error);
        toast({
          title: 'Error',
          description: 'Failed to fetch OpenSolar project details',
          variant: 'destructive',
        });
        throw error;
      }
    },
    enabled: !!projectId,
  });

  // Format date for display
  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handleRefresh = () => {
    refetch();
    toast({
      title: 'Refreshing',
      description: 'Fetching latest project data from OpenSolar',
    });
  };

  return (
    <div className="container py-8">
      {/* Back button and page title */}
      <div className="flex flex-col gap-1 mb-6">
        <div>
          <Button variant="ghost" size="sm" asChild className="mb-2">
            <Link href="/opensolar/projects">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Projects
            </Link>
          </Button>
        </div>
        <div className="flex justify-between items-start">
          <h1 className="text-3xl font-bold">
            {isLoading ? (
              <Skeleton className="h-10 w-64" />
            ) : (
              <>Project: {project?.title || 'Unknown Project'}</>
            )}
          </h1>
          <Button variant="outline" onClick={handleRefresh} disabled={isLoading}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-full max-w-[300px]" />
            </CardHeader>
            <CardContent className="space-y-4">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <Skeleton className="h-6 w-40" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-24 w-full" />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Skeleton className="h-6 w-40" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-24 w-full" />
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Error state */}
      {isError && (
        <Alert variant="destructive" className="mb-6">
          <AlertTitle>Error Loading Project</AlertTitle>
          <AlertDescription>
            {error instanceof Error
              ? error.message
              : 'Failed to load project details. Please try again.'}
          </AlertDescription>
          <div className="mt-4">
            <Button onClick={handleRefresh} variant="outline" size="sm">
              Retry
            </Button>
          </div>
        </Alert>
      )}

      {/* Project data */}
      {!isLoading && !isError && project && (
        <div className="space-y-6">
          {/* Project overview card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Project Overview
              </CardTitle>
              <CardDescription>Basic information about this project</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Status</h3>
                  <StatusBadge status={project.stage} />
                </div>

                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Created</h3>
                  <div className="flex items-center gap-1">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span>{formatDate(project.created_date)}</span>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Last Modified</h3>
                  <div className="flex items-center gap-1">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span>{formatDate(project.modified_date)}</span>
                  </div>
                </div>

                <div className="md:col-span-2 lg:col-span-3">
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">
                    Installation Address
                  </h3>
                  <div className="flex items-start gap-1">
                    <MapPin className="h-4 w-4 text-muted-foreground mt-1 flex-shrink-0" />
                    <span>
                      {project.address}
                      {project.locality && `, ${project.locality}`}
                      {project.state && `, ${project.state}`}
                      {project.zip && ` ${project.zip}`}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
            <CardFooter>
              <a
                href={project.url.replace('/api/', '/')}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full"
              >
                <Button variant="outline" className="w-full">
                  <ExternalLink className="mr-2 h-4 w-4" />
                  View in OpenSolar
                </Button>
              </a>
            </CardFooter>
          </Card>

          {/* Contacts */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Contacts
              </CardTitle>
              <CardDescription>People associated with this project</CardDescription>
            </CardHeader>
            <CardContent>
              {project.contacts_data && project.contacts_data.length > 0 ? (
                <div className="space-y-4">
                  {project.contacts_data.map((contact, index) => (
                    <div key={index} className="rounded-lg border p-4">
                      <h3 className="font-medium mb-2">
                        {contact.first_name} {contact.family_name}
                      </h3>
                      <div className="space-y-2 text-sm">
                        {contact.email && (
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-muted-foreground" />
                            <a
                              href={`mailto:${contact.email}`}
                              className="text-primary hover:underline"
                            >
                              {contact.email}
                            </a>
                          </div>
                        )}
                        {contact.phone && (
                          <div className="flex items-center gap-2">
                            <Phone className="h-4 w-4 text-muted-foreground" />
                            <a
                              href={`tel:${contact.phone}`}
                              className="text-primary hover:underline"
                            >
                              {contact.phone}
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-muted-foreground">
                  No contacts found for this project
                </div>
              )}
            </CardContent>
          </Card>

          {/* Additional details could be added here based on OpenSolar API */}
        </div>
      )}
    </div>
  );
}
